import { endOfYear, isSameDay, isSameYear, startOfYear, today, type CalendarDate } from '@internationalized/date';
import { useContext, useMemo, useState } from 'react';
import { useDateFormatter } from 'react-aria';
import { CalendarStateContext as RACCalendarStateContext } from 'react-aria-components';
import { useLocalizedMessages } from '../../../../i18n/index.js';
import { constrainValue, getEraFormat, isDateInvalid } from '../../utils.js';
import type { ViewGridCellData, ViewGridState } from '../types.js';
import { useDatePickerView } from '../view-provider.js';

const ITEMS_PER_ROW = 4;
const YEARS_PER_SECTION = 20;
const YEARS_SKIP_AMOUNT = 100;

/** @internal */
export function useMultiYearViewState(): ViewGridState {
  const calendarState = useContext(RACCalendarStateContext)!;
  const { switchToView, focusedDate, setFocusedDate } = useDatePickerView();
  const messages = useLocalizedMessages('datePicker');
  const [isFocused, setIsFocused] = useState(true);

  const currentDate = useMemo(() => today(calendarState.timeZone), [calendarState.timeZone]);

  const yearFormatter = useDateFormatter({
    year: 'numeric',
    era: getEraFormat(focusedDate),
    calendar: focusedDate.calendar.identifier,
    timeZone: calendarState.timeZone,
  });

  const lowerYear = (Math.ceil(focusedDate.year / YEARS_PER_SECTION) - 1) * YEARS_PER_SECTION + 1;
  const higherYear = lowerYear + YEARS_PER_SECTION - 1;

  // Years are always represented by positive numbers.
  // If era implies negative years, flip start and end.
  const isNegativeEra = focusedDate.era === 'BC';
  const startYear = isNegativeEra ? higherYear : lowerYear;
  const endYear = isNegativeEra ? lowerYear : higherYear;

  const data: (ViewGridCellData | null)[][] = useMemo(() => {
    const rowCount = Math.ceil(YEARS_PER_SECTION / ITEMS_PER_ROW);
    const result: (ViewGridCellData | null)[][] = new Array(rowCount);

    for (let rowIdx = 0; rowIdx < rowCount; rowIdx++) {
      result[rowIdx] = new Array(ITEMS_PER_ROW).fill(null);
    }

    for (let yearIdx = 0; yearIdx < YEARS_PER_SECTION; yearIdx++) {
      const rowIdx = Math.floor(yearIdx / ITEMS_PER_ROW);
      const colIdx = yearIdx % ITEMS_PER_ROW;

      const yearToSet = startYear + (isNegativeEra ? -yearIdx : yearIdx);
      const date = focusedDate.set({ year: yearToSet });

      if (date.year !== yearToSet) {
        // Reached minimum/maximum date
        continue;
      }

      const content = yearFormatter.format(date.toDate(calendarState.timeZone));

      result[rowIdx]![colIdx] = {
        date,
        content,
        ariaLabel: content,
        isCurrent: isSameYear(date, currentDate),
      };
    }

    return result;
  }, [startYear, calendarState.timeZone, focusedDate, yearFormatter, isNegativeEra, currentDate]);

  const focusCell = (date: CalendarDate) => {
    setFocusedDate(constrainValue(date, calendarState.minValue, calendarState.maxValue));
  };

  return {
    header: yearFormatter.formatRange(
      focusedDate.set({ year: startYear }).toDate(calendarState.timeZone),
      focusedDate.set({ year: endYear }).toDate(calendarState.timeZone),
    ),
    previousLabel: messages.format('previousMultiYearSection', { yearCount: 20 }),
    nextLabel: messages.format('nextMultiYearSection', { yearCount: 20 }),
    data,
    isReadOnly: calendarState.isReadOnly,
    isDisabled: calendarState.isDisabled,
    isFocused,
    setIsFocused,
    focusedDate,
    setFocusedDate,
    timeZone: calendarState.timeZone,
    focusPreviousItem() {
      focusCell(focusedDate.subtract({ years: 1 }));
    },
    focusNextItem() {
      focusCell(focusedDate.add({ years: 1 }));
    },
    focusPreviousRow() {
      focusCell(focusedDate.subtract({ years: ITEMS_PER_ROW }));
    },
    focusNextRow() {
      focusCell(focusedDate.add({ years: ITEMS_PER_ROW }));
    },
    focusSectionStart() {
      focusCell(focusedDate.set({ year: startYear }));
    },
    focusSectionEnd() {
      focusCell(focusedDate.set({ year: endYear }));
    },
    focusPreviousSection(shouldSkip) {
      focusCell(focusedDate.subtract({ years: shouldSkip ? YEARS_SKIP_AMOUNT : YEARS_PER_SECTION }));
    },
    focusNextSection(shouldSkip) {
      focusCell(focusedDate.add({ years: shouldSkip ? YEARS_SKIP_AMOUNT : YEARS_PER_SECTION }));
    },
    isPreviousSectionInvalid() {
      const current = startOfYear(focusedDate.set({ year: startYear }));
      const prev = current.subtract({ days: 1 });

      return (
        isDateInvalid(current, calendarState.minValue, calendarState.maxValue) ||
        isDateInvalid(prev, calendarState.minValue, calendarState.maxValue) ||
        isSameDay(prev, current)
      );
    },
    isNextSectionInvalid() {
      const current = endOfYear(focusedDate.set({ year: endYear }));
      const next = current.add({ days: 1 });

      return (
        isDateInvalid(current, calendarState.minValue, calendarState.maxValue) ||
        isDateInvalid(next, calendarState.minValue, calendarState.maxValue) ||
        isSameDay(next, current)
      );
    },
    selectFocusedDate() {
      switchToView('year');
    },
    isCellDisabled(date) {
      return (
        isDateInvalid(endOfYear(date), calendarState.minValue, null) ||
        isDateInvalid(startOfYear(date), null, calendarState.maxValue)
      );
    },
    isCellSelected(date) {
      return calendarState.value !== null && isSameYear(date, calendarState.value);
    },
    isCellTabbable(date) {
      return isSameYear(date, focusedDate);
    },
  };
}
